import React from 'react';
import { useFinanceStore } from '../api/useFinanceStore';
import { TrendingUp, AlertCircle, DollarSign, PieChart, RefreshCw } from 'lucide-react';
import { formatZAR } from '../../../utils/formatters';

const IntelligenceDashboard: React.FC = () => {
    const { data, variances, error, payments, expenses, refreshData } = useFinanceStore();

    const hasData = data && (data.revenue > 0 || data.expenses > 0 || data.profit !== 0 || data.mrr > 0);

    if (!hasData) {
        const hasFallback = payments.length > 0 || expenses.length > 0;
        return (
            <div className="p-10 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-300">
                {error ? (
                    <>
                        <div className="text-amber-600 font-bold text-sm mb-2">Unable to Load Financial Data</div>
                        <div className="text-slate-400 text-xs">{error}</div>
                        <div className="text-slate-400 text-xs mt-2">Ensure the Financial_Intelligence sheet has data and trigger a sync.</div>
                    </>
                ) : hasFallback ? (
                    <>
                        <div className="text-slate-500 font-bold text-sm mb-2">Partial Financial Data</div>
                        <div className="text-slate-400 text-xs mb-3">Financial_Intelligence tab is empty. Showing computed KPIs from Transactions + Expenses.</div>
                        <button
                            onClick={() => refreshData()}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all"
                        >
                            <RefreshCw size={14} /> Refresh Data
                        </button>
                    </>
                ) : (
                    <>
                        <div className="text-slate-500 font-bold text-sm mb-2">No Financial Data Yet</div>
                        <div className="text-slate-400 text-xs">Sync your Google Sheets to populate the intelligence dashboard.</div>
                        <div className="text-slate-400 text-xs mt-1">The Financial_Intelligence tab should contain Revenue, Expenses, Profit, MRR, ARR, and GrowthRate columns.</div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Financial Intelligence • {data.period}
                </div>
                <button
                    onClick={() => refreshData()}
                    className="p-2 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-all"
                    title="Refresh"
                >
                    <RefreshCw size={14} />
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Top Metrics */}
                <div className="p-6 bg-white rounded-3xl shadow-sm border border-slate-200 group hover:border-blue-300 transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
                            <DollarSign size={20} />
                        </div>
                        <span className="text-[10px] font-black text-green-600 uppercase tracking-widest flex items-center gap-1">
                            <TrendingUp size={12} /> {data.growthRate}%
                        </span>
                    </div>
                    <div className="text-3xl font-black text-slate-800">{formatZAR(data.mrr)}</div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Monthly Recurring Revenue</div>
                </div>
                <div className="p-6 bg-white rounded-3xl shadow-sm border border-slate-200 group hover:border-blue-300 transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                            <TrendingUp size={20} />
                        </div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Projected</span>
                    </div>
                    <div className="text-3xl font-black text-slate-800">{formatZAR(data.arr)}</div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Annual Recurring Revenue</div>
                </div>
                <div className="p-6 bg-white rounded-3xl shadow-sm border border-slate-200 group hover:border-blue-300 transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-50 text-green-600 rounded-2xl">
                            <PieChart size={20} />
                        </div>
                        <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Net Margin</span>
                    </div>
                    <div className="text-3xl font-black text-slate-800">{formatZAR(data.profit)}</div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Net Operating Profit</div>
                </div>
            </div>

            {/* Budget Variance Analysis */}
            <div className="p-6 bg-white rounded-3xl shadow-sm border border-slate-200">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">Budget Variance Analysis</h3>
                    <div className="flex items-center gap-2 text-amber-600 text-[10px] font-black uppercase tracking-widest">
                        <AlertCircle size={14} /> Real-time Alerts
                    </div>
                </div>
                <div className="grid gap-4">
                    {variances.length === 0 ? (
                        <div className="text-xs text-slate-400 italic py-2">No budget variance data available. Add data to the Budgets tab.</div>
                    ) : (
                        variances.map(v => (
                            <div key={v.category} className="p-4 bg-slate-50 rounded-2xl flex items-center justify-between group hover:bg-slate-100 transition-all">
                                <div className="text-sm font-bold text-slate-700">{v.category}</div>
                                <div className="flex items-center gap-6">
                                    <div className="text-right">
                                        <div className="text-xs font-black text-slate-800">{formatZAR(v.actual)}</div>
                                        <div className="text-[10px] text-slate-400 font-medium">Budget: {formatZAR(v.budgeted)}</div>
                                    </div>
                                    <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${v.status === 'critical' ? 'bg-red-100 text-red-600' : v.status === 'warning' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`}>
                                        {v.variance > 0 ? `+${formatZAR(v.variance)}` : `-${formatZAR(Math.abs(v.variance))}`}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default IntelligenceDashboard;
